// Local-mode mocks: [{ pattern, respond(body) }], first match wins.
//
// POST /api/v1/audit/list (authnull-service internal/adminaudit/list.go):
// filters actor (email contains, case-insensitive), outcome, route (exact),
// targetType, targetId, from (inclusive), to (exclusive); page / pageSize
// (default 50, max 500); newest first. → { total, page, pageSize, events }.

const HOUR = 3600 * 1000;
const NOW = Date.now();

const ACTORS = {
  sathyam: { actorUserId: 31, actorEmail: "sathyam@authnull.com", clientIp: "192.168.122.175" },
  aditya: { actorUserId: 12, actorEmail: "aditya@authnull.com", clientIp: "203.0.113.24" },
  priya: { actorUserId: 44, actorEmail: "priya.nair@authnull.com", clientIp: "198.51.100.7" },
  bot: { actorUserId: 57, actorEmail: "", clientIp: "10.0.4.12" },
  viewer: { actorUserId: 63, actorEmail: "eve.viewer@authnull.com", clientIp: "198.51.100.88" },
};

const UA_CHROME = "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/131.0 Safari/537.36";
const UA_FIREFOX = "Mozilla/5.0 (X11; Linux x86_64; rv:133.0) Gecko/20100101 Firefox/133.0";
const UA_SCRIPT = "python-requests/2.32.3";

const POLICY_IDS = {
  p2: "6f1c2a34-9b7d-4e21-8c55-1d2e3f4a5b61",
  readers: "0a9b8c7d-6e5f-4a3b-9c2d-1e0f2a3b4c5d",
  break: "c3d4e5f6-a7b8-4c9d-8e0f-1a2b3c4d5e6f",
  legacy: "11112222-3333-4444-8555-666677778888",
  ssh: "aaaabbbb-cccc-4ddd-8eee-ffff00001111",
};

const dbPolicy = (name, { action = "mfa_required", users = ["dbmfa_reader"], databases = ["dbmfa_test"], hours = null } = {}) => ({
  name,
  policyType: "database",
  rules: [
    {
      action,
      match: { dbUsers: users, databases, dbType: "postgres" },
      conditions: hours ? { timeOfDay: { from: hours[0], to: hours[1], tz: "Asia/Kolkata" } } : {},
      mfa: action === "mfa_required" ? { methods: ["push"], timeoutSeconds: 60 } : undefined,
    },
  ],
  assignedTo: { groups: ["db-admins"], users: [] },
});

const snap = (name, status, policyOpts) => ({ name, type: "database", status, policy: dbPolicy(name, policyOpts) });

const P = "/api/v1/policy/json";

// One template per row of the action mapping table (and a few extras), each
// with its own change shape. `outcome` defaults to success.
const TEMPLATES = [
  {
    route: `${P}/updatePolicy`,
    targetType: "policy",
    targetId: POLICY_IDS.p2,
    changes: {
      before: snap("dbmfa-P2", "Approved", { users: ["dbmfa_reader"] }),
      after: snap("dbmfa-P2", "Approved", { users: ["dbmfa_reader", "dbmfa_writer"], hours: ["09:00", "18:00"] }),
    },
  },
  {
    route: `${P}/approvePolicy`,
    targetType: "policy",
    targetId: POLICY_IDS.p2,
    changes: { before: snap("dbmfa-P2", "Suspended"), after: snap("dbmfa-P2", "Approved") },
  },
  {
    route: `${P}/deletePolicy`,
    targetType: "policy",
    targetId: POLICY_IDS.legacy,
    changes: { before: snap("legacy-readonly", "Approved", { action: "allow", users: ["report_ro"] }) },
  },
  {
    route: "/api/v1/policyService/deletePolicyJSON",
    targetType: "policy",
    targetId: POLICY_IDS.ssh,
    changes: { before: { name: "ssh-prod-bastion", type: "ssh", status: "Suspended", policy: { name: "ssh-prod-bastion", hosts: ["10.0.2.0/24"], mfa: true } } },
  },
  {
    route: `${P}/revokePolicy`,
    targetType: "policy",
    targetId: POLICY_IDS.break,
    changes: { before: snap("break-glass-dba", "Approved", { action: "allow", users: ["postgres"] }), after: snap("break-glass-dba", "Revoked", { action: "allow", users: ["postgres"] }) },
  },
  { route: `${P}/createPolicy`, targetType: "policy", targetId: "" },
  { route: "/api/v1/policy/changeStatus", targetType: "policy", targetId: POLICY_IDS.readers },
  {
    route: "/api/v1/databaseService/settings/noPolicy",
    action: "database.noPolicy.set",
    targetType: "tenant",
    targetId: "1",
    changes: { before: { noPolicyAction: "allow" }, after: { noPolicyAction: "mfa" } },
  },
  {
    route: "/api/v1/databaseService/users/password/manage",
    action: "database.userPassword.manage",
    targetType: "dbRole",
    targetId: "6/ca_elev",
    changes: { after: { enabled: true } },
  },
  {
    route: "/api/v1/databaseService/users/password/manage",
    action: "database.userPassword.manage",
    targetType: "dbRole",
    targetId: "6/vault_demo",
    changes: { after: { enabled: false } },
  },
  {
    route: "/api/v1/databaseService/users/password/rotate",
    action: "database.userPassword.rotate",
    targetType: "dbRole",
    targetId: "6/ca_elev",
  },
  {
    route: "/api/v1/databaseService/settings/passwordRotation",
    changes: { before: { passwordRotationHours: 24 }, after: { passwordRotationHours: 168 } },
  },
  { route: "/api/v1/databaseService/proxy/credentials" },
  { route: "/api/v1/database/proxy/credentials/revoke" },
  { route: "/api/v1/tenants/updateLogOutputConfiguration" },
  { route: "/api/v1/tenants/storeLogOutputConfiguration" },
  // Not in the mapping table: shown as the raw action.
  { route: "/api/v1/users/addUser" },
  { route: "/api/v1/recordingStorage/save", method: "POST" },
];

// Template index, actor, outcome, hours ago. ~60 events over 10 days, newest
// first once sorted. Failures (4xx/5xx) and denials (401/403) sprinkled in.
const PLAN = [
  [1, "sathyam", "success", 0.4],
  [0, "sathyam", "success", 1.2],
  [8, "aditya", "success", 2.1],
  [10, "aditya", "success", 2.3],
  [11, "aditya", "success", 3.5],
  [7, "sathyam", "success", 5],
  [6, "viewer", "denied", 6.2],
  [12, "priya", "success", 8],
  [14, "priya", "failure", 9.1],
  [14, "priya", "success", 9.4],
  [16, "aditya", "success", 12],
  [2, "sathyam", "success", 14.5],
  [5, "sathyam", "success", 18],
  [4, "aditya", "success", 22],
  [9, "aditya", "success", 26],
  [1, "viewer", "denied", 27],
  [0, "priya", "failure", 30],
  [0, "priya", "success", 30.2],
  [3, "sathyam", "success", 34],
  [13, "aditya", "success", 40],
  [15, "priya", "success", 44],
  [17, "bot", "success", 47],
  [10, "bot", "success", 48],
  [10, "bot", "failure", 52],
  [6, "sathyam", "success", 55],
  [8, "viewer", "denied", 58],
  [1, "aditya", "success", 63],
  [0, "aditya", "success", 66],
  [11, "sathyam", "success", 71],
  [7, "priya", "success", 76],
  [12, "sathyam", "success", 80],
  [16, "sathyam", "failure", 84],
  [5, "priya", "success", 90],
  [0, "sathyam", "success", 96],
  [1, "sathyam", "success", 99],
  [4, "aditya", "failure", 104],
  [2, "aditya", "success", 110],
  [9, "sathyam", "success", 117],
  [14, "viewer", "denied", 122],
  [13, "sathyam", "success", 128],
  [10, "bot", "success", 134],
  [6, "aditya", "success", 140],
  [17, "priya", "success", 146],
  [3, "priya", "success", 152],
  [0, "priya", "success", 158],
  [8, "sathyam", "success", 165],
  [11, "aditya", "failure", 171],
  [15, "sathyam", "success", 178],
  [5, "aditya", "success", 184],
  [1, "priya", "success", 190],
  [12, "viewer", "denied", 196],
  [16, "aditya", "success", 203],
  [7, "aditya", "success", 210],
  [10, "bot", "success", 216],
  [0, "sathyam", "success", 222],
  [9, "priya", "success", 227],
  [2, "priya", "success", 231],
  [4, "sathyam", "success", 234],
  [13, "priya", "success", 236],
  [6, "sathyam", "success", 238],
];

const STATUS = { success: 200, failure: 400, denied: 403 };

const uuid = (n) => {
  const h = (n * 2654435761) >>> 0;
  const hex = (v, len) => (v >>> 0).toString(16).padStart(len, "0").slice(-len);
  return `${hex(h, 8)}-${hex(h >> 4, 4)}-4${hex(n, 3)}-8${hex(h >> 8, 3)}-${hex(h ^ 0x5a5a5a5a, 8)}${hex(n, 4)}`;
};

const EVENTS = PLAN.map(([t, who, outcome, hoursAgo], i) => {
  const tpl = TEMPLATES[t];
  const a = ACTORS[who];
  const method = tpl.method || "POST";
  const status = who === "bot" && outcome === "failure" ? 500 : STATUS[outcome];
  // Refused and failed requests change nothing, so they carry no changes.
  const changes = outcome === "success" && tpl.changes ? JSON.parse(JSON.stringify(tpl.changes)) : undefined;
  return {
    eventId: uuid(i + 1),
    tenantId: 1,
    at: new Date(NOW - hoursAgo * HOUR - (i % 7) * 61 * 1000).toISOString(),
    actorUserId: a.actorUserId,
    ...(a.actorEmail ? { actorEmail: a.actorEmail } : {}),
    clientIp: a.clientIp,
    userAgent: who === "bot" ? UA_SCRIPT : who === "priya" ? UA_FIREFOX : UA_CHROME,
    method,
    route: tpl.route,
    status,
    outcome,
    action: tpl.action || `${method} ${tpl.route}`,
    ...(tpl.targetType ? { targetType: tpl.targetType, targetId: tpl.targetId || "" } : {}),
    ...(changes ? { changes } : {}),
  };
}).sort((x, y) => (x.at < y.at ? 1 : x.at > y.at ? -1 : 0));

export const AUDIT_LOG_EVENTS = EVENTS;

const str = (v) => (typeof v === "string" ? v.trim() : "");

export function listAuditMock(body = {}) {
  const b = body || {};
  const actor = str(b.actor).toLowerCase();
  const from = str(b.from) ? Date.parse(b.from) : null;
  const to = str(b.to) ? Date.parse(b.to) : null;
  const rows = EVENTS.filter((e) => {
    if (actor && !String(e.actorEmail || "").toLowerCase().includes(actor)) return false;
    if (str(b.outcome) && e.outcome !== b.outcome) return false;
    if (str(b.route) && e.route !== str(b.route)) return false;
    if (str(b.targetType) && e.targetType !== str(b.targetType)) return false;
    if (str(b.targetId) && e.targetId !== str(b.targetId)) return false;
    const at = Date.parse(e.at);
    if (from != null && !Number.isNaN(from) && at < from) return false;
    if (to != null && !Number.isNaN(to) && at >= to) return false;
    return true;
  });
  const page = Math.max(1, parseInt(b.page, 10) || 1);
  const pageSize = Math.min(500, Math.max(1, parseInt(b.pageSize, 10) || 50));
  return { total: rows.length, page, pageSize, events: rows.slice((page - 1) * pageSize, page * pageSize) };
}

export const AUDIT_LOG_MOCKS = [{ pattern: "/api/v1/audit/list", respond: (body) => listAuditMock(body) }];
